import { useSelector } from 'react-redux';
import { IconButton, IconName } from '@optimus-web/core';
import { useDialogAction } from '@mews/framework';
import { ApplicationState } from '@mews-commander/types';
import { RateGroupWithPaymentPolicies } from '@mews-commander/data-access';
import { useEditLocalRateGroup } from '../../../../queries';
import { WithEnterpriseIds } from '../../../../types';
import { getEnterprisesUniqDefaultLanguageCodes } from '../../../../selectors';
import { keyByLanguageCodes } from '../rateForm/helpers';
import { EditGeneralDetailsDialog } from './EditGeneralDetailsDialog';
import { GeneralDetailsMetaFormValues } from './formEnums';

type Props = WithEnterpriseIds & {
    rateGroup: RateGroupWithPaymentPolicies;
    linkedRateGroups: number;
    isPortfolioManaged?: boolean;
};

export const EditRateGroupContainer = ({ rateGroup, isPortfolioManaged, enterpriseIds, linkedRateGroups }: Props) => {
    const enterprisesDefaultlanguageCodes = useSelector<ApplicationState, string[]>((state) =>
        getEnterprisesUniqDefaultLanguageCodes(state, { ids: enterpriseIds })
    );
    const { states, handlers } = useDialogAction(async () => Promise.resolve());
    const { mutateAsync: mutateLocalRateGroup, isPending: isLocalRateGroupLoading } = useEditLocalRateGroup();

    const handleOnSubmit = async (values: GeneralDetailsMetaFormValues) => {
        await mutateLocalRateGroup({
            serviceId: rateGroup.serviceId,
            rateGroupUpdates: [
                {
                    rateGroupId: rateGroup.id,
                    names: { value: keyByLanguageCodes(enterprisesDefaultlanguageCodes, values.name) },
                    descriptions: values.description
                        ? {
                              value: keyByLanguageCodes(enterprisesDefaultlanguageCodes, values.description),
                          }
                        : {
                              value: {},
                          },
                    shortNames: values.shortName
                        ? {
                              value: keyByLanguageCodes(enterprisesDefaultlanguageCodes, values.shortName),
                          }
                        : { value: {} },
                    ...(isPortfolioManaged
                        ? {}
                        : {
                              externalIdentifier: {
                                  value: values.rateGroupKey,
                              },
                          }),
                    ordering: {
                        value: values.ordering,
                    },
                    settlementRuleUpdates: [],
                },
            ],
        });

        await handlers.handleConfirm();
    };

    return (
        <>
            <IconButton icon={IconName.Edit} onClick={handlers.handleOpen} type="submit" />
            <EditGeneralDetailsDialog
                isLoading={isLocalRateGroupLoading}
                isOpen={states.isDialogOpened}
                rateGroup={rateGroup}
                linkedRateGroups={linkedRateGroups}
                isPortfolioManaged={isPortfolioManaged}
                onSubmit={handleOnSubmit}
                onClose={handlers.handleCancel}
            />
        </>
    );
};
